export interface CustomShortcut {
  name: string;           // Display name for the button
  location: string;       // User input text
  submittedLocation: string;  // API display name
  lat: number;
  lng: number;
  timestamp: number;      // When saved
  expiresAt: number;     // 30 days from save
}

const STORAGE_KEY = 'sunrise-sunset-custom-shortcuts';
const EXPIRATION_DAYS = 30;
const MAX_SHORTCUTS = 2;

/**
 * Get all custom shortcuts from localStorage
 */
export const getCustomShortcuts = (): CustomShortcut[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      return [];
    }
    
    const shortcuts: CustomShortcut[] = JSON.parse(saved);
    const now = Date.now();
    
    // Filter out expired shortcuts
    const validShortcuts = shortcuts.filter(shortcut => now <= shortcut.expiresAt);
    
    // If any were filtered out, update localStorage
    if (validShortcuts.length !== shortcuts.length) {
      saveCustomShortcuts(validShortcuts);
    }
    
    return validShortcuts;
  } catch (error) {
    console.warn('Failed to get custom shortcuts from localStorage:', error);
    return [];
  }
};

/**
 * Save custom shortcuts array to localStorage
 */
const saveCustomShortcuts = (shortcuts: CustomShortcut[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(shortcuts));
  } catch (error) {
    console.warn('Failed to save custom shortcuts to localStorage:', error);
  }
};

/**
 * Add a new custom shortcut
 * Returns true if successful, false if max shortcuts reached
 */
export const addCustomShortcut = (
  name: string,
  location: string,
  submittedLocation: string,
  lat: number,
  lng: number
): boolean => {
  try {
    const shortcuts = getCustomShortcuts();
    
    // Check if we've reached the maximum
    if (shortcuts.length >= MAX_SHORTCUTS) {
      return false;
    }
    
    // Check if this location already exists (by name or coordinates)
    const exists = shortcuts.some(
      s => s.name === name || 
      (Math.abs(s.lat - lat) < 0.001 && Math.abs(s.lng - lng) < 0.001)
    );
    
    if (exists) {
      return false; // Already exists
    }
    
    const now = Date.now();
    const expiresAt = now + (EXPIRATION_DAYS * 24 * 60 * 60 * 1000);
    
    const newShortcut: CustomShortcut = {
      name,
      location,
      submittedLocation,
      lat,
      lng,
      timestamp: now,
      expiresAt
    };
    
    shortcuts.push(newShortcut);
    saveCustomShortcuts(shortcuts);
    return true;
  } catch (error) {
    console.warn('Failed to add custom shortcut:', error);
    return false;
  }
};

/**
 * Remove a custom shortcut by name
 */
export const removeCustomShortcut = (name: string): void => {
  try {
    const shortcuts = getCustomShortcuts();
    const filtered = shortcuts.filter(s => s.name !== name);
    saveCustomShortcuts(filtered);
  } catch (error) {
    console.warn('Failed to remove custom shortcut:', error);
  }
};

/**
 * Check if we can add more custom shortcuts
 */
export const canAddMoreShortcuts = (): boolean => {
  return getCustomShortcuts().length < MAX_SHORTCUTS;
};

/**
 * Clear all custom shortcuts
 */
export const clearAllCustomShortcuts = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear custom shortcuts from localStorage:', error);
  }
};

